import { Functions as SigmaIcon, Lock as LockIcon } from "@mui/icons-material";
import { Box, Tooltip } from "@mui/material";
import { green, grey } from "@mui/material/colors";
import { isNil } from "lodash";
import { useMemo } from "react";
import { useField } from "react-final-form";
import RffBooleanCheckbox from "../../finalForm/inputs/RffBooleanCheckbox";
import RffDateField from "../../finalForm/inputs/RffDateField";
import RffDollarField from "../../finalForm/inputs/RffDollarField";
import RffFloatField from "../../finalForm/inputs/RffFloatField";
import RffNumberField from "../../finalForm/inputs/RffNumberField";
import RffPercentField from "../../finalForm/inputs/RffPercentField";
import RffSelectField from "../../finalForm/inputs/RffSelectField";
import RffSelectMultipleCheckboxField from "../../finalForm/inputs/RffSelectMultipleCheckboxField";
import RffTextField from "../../finalForm/inputs/RffTextField";
import Flx from "../../layout/Flx";
import Txt from "../../typography/Txt";
import DetailRowLabel from "./DetailRowLabel";
import { fieldPassesSearch } from "./sidebarFieldUtils";
import FlxGrid from "../../../_src_shared/components/layout/FlxGrid";

const FONT_MONO = "ui-monospace, Menlo, monospace";

const RffEditor = ({ name, editor }) => {
  switch (editor?.type) {
    case "dollar":
      return <RffDollarField name={name} suppressGrid label={null} />;
    case "number":
      return <RffNumberField name={name} suppressGrid label={null} />;
    // Points, not a row count — verified experience carries half points.
    case "float":
      return <RffFloatField name={name} suppressGrid label={null} />;
    case "percent":
      return <RffPercentField name={name} suppressGrid label={null} />;
    case "date":
      return <RffDateField name={name} suppressGrid label={null} />;
    case "boolean":
      return <RffBooleanCheckbox name={name} suppressGrid row label={null} />;
    case "select":
      return (
        <RffSelectField
          name={name}
          suppressGrid
          label={null}
          options={editor.options || []}
        />
      );
    case "selectMultiple":
      return (
        <RffSelectMultipleCheckboxField
          name={name}
          suppressGrid
          label={null}
          options={editor.options || []}
          placeholder={editor.placeholder}
        />
      );
    case "text":
    default:
      return <RffTextField name={name} suppressGrid label={null} />;
  }
};

const InlineEditableRow = ({
  label,
  fontSize,
  bodyPx,
  name,
  editor,
  computed,
  computedTooltip,
  searchTerms,
  quickFilter,
}) => {
  const { meta } = useField(name, {
    subscription: { dirty: true },
  });
  const isPending = meta.dirty;

  const visible = useMemo(
    () => fieldPassesSearch({ label, searchTerms }, quickFilter),
    [label, searchTerms, quickFilter],
  );
  if (!visible) return null;

  const valueBg = isPending ? "#fff7e6" : "#e3f2fd5c";
  // const valueBg = isPending ? "#fff7e6" : "#e3f2fd35";
  const valueHoverBg = isPending ? "#fff1d4" : "#e3f2fd66";

  return (
    <FlxGrid
      width="180px"
      sx={{
        alignItems: "stretch",
        mx: -bodyPx,
        gap: 0,
        minHeight: 32,
        position: "relative",
        borderBottom: "1px dashed #dde0e4",
        borderLeft: isPending ? "3px solid #f59e0b" : "3px solid transparent",
        // pl: isPending ? 1.4 : 0,
        background: "#fff",
      }}
    >
      <Flx
        ac
        g={0.5}
        sx={{
          flexShrink: 0,
          pl: bodyPx,
          pr: 1.25,
          py: 0.6,
          background: "#fff",
        }}
      >
        <DetailRowLabel fontSize={fontSize}>{label}</DetailRowLabel>
        {isPending && (
          <Txt
            sx={{
              fontSize: 9.5,
              fontWeight: 400,
              color: "#b45309",
              bgcolor: "#fde68a",
              px: 0.6,
              py: 0.1,
              borderRadius: "4px",
              letterSpacing: "0.04em",
              textTransform: "uppercase",
            }}
          >
            Modified
          </Txt>
        )}
      </Flx>

      <Flx
        ac
        g={0.5}
        sx={{
          flex: 1,
          minWidth: 0,
          // pl: 1.25,
          pr: bodyPx,
          py: 0.3,
          justifyContent: "flex-end",
          background: valueBg,
          border: "1px solid #e3f2fdff",
          transition: "background 0.12s",
          "&:hover": { background: valueHoverBg },
          ".rff-input-row, .input-row-root, .input-wrapper-root": {
            border: "none !important",
            paddingBottom: "0 !important",
          },
        }}
      >
        {computed && (
          <Tooltip
            title={
              <Box
                sx={{
                  whiteSpace: "pre-line",
                  fontFamily: FONT_MONO,
                  fontSize: 11.5,
                  lineHeight: 1.5,
                }}
              >
                {computedTooltip || "Auto-calculated value"}
              </Box>
            }
            arrow
            placement="left"
          >
            <SigmaIcon
              sx={{ fontSize: 13, color: green[400], cursor: "help" }}
            />
          </Tooltip>
        )}
        <Box sx={{ flex: 1, minWidth: 0, maxWidth: 320, ml: "auto" }}>
          <RffEditor name={name} editor={editor} />
        </Box>
      </Flx>
    </FlxGrid>
  );
};

const StaticRow = ({
  label,
  value,
  editable,
  computed,
  computedTooltip,
  fontSize,
  bodyPx,
  searchTerms,
  quickFilter,
}) => {
  const fieldValue = useMemo(() => {
    if (isNil(value) || value === "—") return;
    return value;
  }, [value]);

  const visible = useMemo(
    () =>
      fieldPassesSearch({ label, value: fieldValue, searchTerms }, quickFilter),
    [label, fieldValue, searchTerms, quickFilter],
  );
  if (!visible) return null;

  const valueBg = editable ? "#e3f2fd35" : "transparent";
  const valueHoverBg = editable ? "#e3f2fd66" : "transparent";

  return (
    <Flx
      sx={{
        alignItems: "stretch",
        mx: -bodyPx,
        gap: 0,
        minHeight: 32,
        position: "relative",
        borderBottom: "1px dashed #dde0e4",
        borderLeft: "3px solid transparent",
        background: "#fff",
      }}
    >
      <Flx
        ac
        g={0.5}
        sx={{
          flexShrink: 0,
          pl: bodyPx,
          pr: 1.25,
          py: 0.6,
          background: "#fff",
        }}
      >
        <DetailRowLabel fontSize={fontSize}>{label}</DetailRowLabel>
      </Flx>

      <Flx
        ac
        g={0.5}
        sx={{
          flex: 1,
          minWidth: 0,
          pl: 1.25,
          pr: bodyPx,
          py: 0.6,
          justifyContent: "flex-end",
          background: valueBg,
          transition: "background 0.12s",
          "&:hover": { background: valueHoverBg },
        }}
      >
        {computed ? (
          <Tooltip
            title={
              <Box
                sx={{
                  whiteSpace: "pre-line",
                  fontFamily: FONT_MONO,
                  fontSize: 11.5,
                  lineHeight: 1.5,
                }}
              >
                {computedTooltip || "Auto-calculated value"}
              </Box>
            }
            arrow
            placement="left"
          >
            <SigmaIcon
              sx={{ fontSize: 13, color: green[400], cursor: "help" }}
            />
          </Tooltip>
        ) : !editable ? (
          <Tooltip title="This field cannot be edited" arrow placement="left">
            <LockIcon
              sx={{
                fontSize: 12,
                color: grey[500],
                cursor: "help",
                opacity: 0.55,
              }}
            />
          </Tooltip>
        ) : null}
        <Txt
          sx={{
            fontSize: fontSize,
            fontWeight: isNil(fieldValue) ? 300 : 500,
            color: "#1e293b",
            opacity: isNil(fieldValue) ? 0.5 : 1,
            textAlign: "right",
            wordBreak: "break-word",
          }}
        >
          {fieldValue ?? "—"}
        </Txt>
      </Flx>
    </Flx>
  );
};

const DetailRow = ({
  label,
  value,
  editable,
  computed,
  computedTooltip,
  fontSize = 11.5,
  bodyPx = 1,
  // inline edit
  name,
  editor,
  // filter props
  fieldType,
  searchTerms,
  quickFilter,
}) => {
  const renderInline = Boolean(editable && name && editor);
  if (renderInline) {
    return (
      <InlineEditableRow
        label={label}
        fontSize={fontSize}
        bodyPx={bodyPx}
        name={name}
        editor={editor}
        computed={computed}
        computedTooltip={computedTooltip}
        searchTerms={searchTerms}
        quickFilter={quickFilter}
      />
    );
  }
  return (
    <StaticRow
      label={label}
      value={value}
      editable={editable}
      computed={computed}
      computedTooltip={computedTooltip}
      fontSize={fontSize}
      bodyPx={bodyPx}
      searchTerms={searchTerms}
      quickFilter={quickFilter}
    />
  );
};

export default DetailRow;
